// src/pages/ApprovedSubmissions.tsx
import appSettings from "@/appsettings.json";
import { SessionContext } from "@/context/SessionContext";
import { InstrumentalistTask, ProjectSubmission } from "@/javascript/project-objects";
import { User } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import {
	Badge,
	Box,
	Button,
	Group,
	Pagination,
	ScrollArea,
	Skeleton,
	Stack,
	Table,
	Text,
	Tooltip,
	useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
	IconAlertCircle,
	IconPlayerPlay,
	IconRefresh,
	IconUser,
	IconUsers,
} from "@tabler/icons-react";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import VideoModal from "../conduction-social/VideoModal";
import { Countdown } from "../misc/Countdown";

/* ----------------------------- constants/helpers ---------------------------- */

const HEADER_FW = 700;

// Column widths
const CW = {
	desktop: {
		parent: {
			dueDate: "23%",
			studentName: "23%",
			projectName: "23%",
			spacer: "8%",
		},
		child: {
			indent: "16%",
			instrumentName: "22%",
			assignee: "28%",
			fulfilled: "34%",
		},
	},
	mobile: {
		parent: {
			dueDate: "28%",
			names: "52%",
			spacer: "20%",
		},
		child: {
			indent: "0%",
			instrumentName: "28%",
			assignee: "38%",
			fulfilled: "34%",
		},
	},
} as const;

// Defensive date parse
const asDate = (d: Date | string | null | undefined): Date | null => {
	if (!d) return null;
	if (d instanceof Date) return isNaN(+d) ? null : d;
	const parsed = new Date(d);
	return isNaN(+parsed) ? null : parsed;
};

// Local formatting
const toLocal = (d: Date | string | null | undefined, isMobile = false): string => {
	const date = asDate(d);
	if (!date) return "—";
	const optsMobile: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
	const optsDesktop: Intl.DateTimeFormatOptions = {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	};
	return date.toLocaleString(undefined, isMobile ? optsMobile : optsDesktop);
};

const truncate = (s: string, max = 10) =>
	(s?.length ?? 0) > max ? s.slice(0, max) + "..." : s ?? "";

const statusColor = (
	theme: ReturnType<typeof useMantineTheme>,
	fulfilled: Date | string | null | undefined,
	due: Date | string | null | undefined
) => {
	const f = asDate(fulfilled);
	if (f) return theme.colors.green[6];
	const dueD = asDate(due);
	if (!dueD) return theme.colors.blue[6];
	return dueD < new Date() ? theme.colors.red[6] : theme.colors.blue[6];
};

/* --------------------------- inline offered users cell --------------------------- */

const OfferedOrAssigneeCell: React.FC<{
	assignee?: User | null;
	offered?: User[] | null | undefined;
	deadline?: Date | string | null | undefined;
	isMobile: boolean;
	fzSmall: number;
}> = ({ assignee, offered, deadline, isMobile, fzSmall }) => {
	// Keep the row height stable by constraining the scrollable list to ~row height.
	// (Approximate based on font size; can be tweaked if you style rows taller.)
	const rowMah = isMobile ? 40 : 56;

	if (assignee) {
		return (
			<Text fz={fzSmall}>
				{Utils.formatUser(assignee)}
				{assignee.email ? ` (${assignee.email})` : ""}
			</Text>
		);
	}

	// No assignee yet → show offered users scroller + countdown (side-by-side)
	const list = Array.isArray(offered) ? offered : [];
	const hasList = list.length > 0;

	return (
		<Group align="center" justify="space-between" wrap="nowrap" gap="xs">
			<ScrollArea.Autosize mah={rowMah} type="auto" style={{ flex: 1 }}>
				<Stack gap={4} pr={6}>
					{hasList ? (
						list.map((u) => (
							<Badge
								key={u.id}
								variant="light"
								leftSection={<IconUser size={12} />}
								styles={{ label: { whiteSpace: "nowrap" } }}
							>
								{isMobile ? truncate(Utils.formatUser(u), 14) : Utils.formatUser(u)}
							</Badge>
						))
					) : (
						<Text fz={fzSmall} c="dimmed">
							—
						</Text>
					)}
				</Stack>
			</ScrollArea.Autosize>

			{/* Countdown sits "next to" the list */}
			{deadline ? (
				<Box pl={6} ta="center">
					<Countdown targetDate={deadline} activeTimer textSz={isMobile ? 10 : 12} pastDueText="0:00" />
				</Box>
			) : null}
		</Group>
	);
};

/* -------------------------------- subcomponents ----------------------------- */

const TaskRow: React.FC<{
	isMobile: boolean;
	fzSmall: number;
	parent: ProjectSubmission;
	task: InstrumentalistTask;
}> = ({ isMobile, fzSmall, parent, task }) => {
	const [videoModalOpened, setVideoModalOpen] = useState<boolean>(false);
	const theme = useMantineTheme();

	const fulfilledColor = statusColor(theme, task.dateFulfilled, parent.instrumentalistDueDate);

	return (
		<Table.Tr key={task.id}>
			{/* Indent */}
			<Table.Td w={isMobile ? CW.mobile.child.indent : CW.desktop.child.indent} />

			{/* Instrument */}
			<Table.Td
				fz={fzSmall}
				w={isMobile ? CW.mobile.child.instrumentName : CW.desktop.child.instrumentName}
				ta="center"
			>
				{isMobile ? truncate(task.instrument.name, 16) : task.instrument.name}
			</Table.Td>

			{/* Assignee OR Offered list + countdown (inline) */}
			<Table.Td fz={fzSmall} w={isMobile ? CW.mobile.child.assignee : CW.desktop.child.assignee}>
				<OfferedOrAssigneeCell
					assignee={task.instrumentalist ?? null}
					offered={task.instrumentalistsToOffer}
					deadline={task.currentOfferDeadline}
					isMobile={isMobile}
					fzSmall={fzSmall}
				/>
			</Table.Td>

			{/* Submitted */}
			<Table.Td
				fz={fzSmall}
				w={isMobile ? CW.mobile.child.fulfilled : CW.desktop.child.fulfilled}
				ta="center"
				c={fulfilledColor}
				style={{ whiteSpace: "nowrap" }}
			>
				<Group justify="center">
					{toLocal(task.dateFulfilled, isMobile)}
					{task.taskStatus === "Uploaded" && (
						<>
							<Tooltip label="Watch clip">
								<Button
									onClick={() => setVideoModalOpen(true)}
									size={isMobile ? "compact-xs" : "compact-sm"}
									variant="default"
								>
									<IconPlayerPlay size={isMobile ? 14 : 16} />
								</Button>
							</Tooltip>
							<VideoModal
								videoPath={`${appSettings.S3BucketBaseUrl}InstrumentalistVideos/${task.id}.mp4`}
								isOpen={videoModalOpened}
								setShowModal={setVideoModalOpen}
							/>
						</>
					)}
				</Group>
			</Table.Td>
		</Table.Tr>
	);
};

const EditorRow: React.FC<{
	isMobile: boolean;
	fzSmall: number;
	parent: ProjectSubmission;
}> = ({ isMobile, fzSmall, parent }) => {
	const [videoModalOpened, setVideoModalOpen] = useState<boolean>(false);
	const theme = useMantineTheme();
	const color = statusColor(theme, parent.dateFulfilled, parent.editorDueDate);

	const offeredEditors = parent.editorsToOffer;
	const editorOfferDeadline = parent.currentOfferDeadline as Date | string | null | undefined;

	return (
		<Table.Tr>
			{/* Indent */}
			<Table.Td w={isMobile ? CW.mobile.child.indent : CW.desktop.child.indent} />

			{/* Label as "Role" col to keep grid aligned */}
			<Table.Td
				fz={fzSmall}
				w={isMobile ? CW.mobile.child.instrumentName : CW.desktop.child.instrumentName}
				ta="center"
			>
				<Text fz={Math.max(10, fzSmall * 0.95)} c={theme.colors.blue[6]} fw={500}>
					Video Editing
				</Text>
			</Table.Td>

			{/* Assignee OR Offered editors + countdown (inline) */}
			<Table.Td fz={fzSmall} w={isMobile ? CW.mobile.child.assignee : CW.desktop.child.assignee}>
				<OfferedOrAssigneeCell
					assignee={parent.videoEditor ?? null}
					offered={offeredEditors}
					deadline={editorOfferDeadline}
					isMobile={isMobile}
					fzSmall={fzSmall}
				/>
			</Table.Td>

			{/* Submitted */}
			<Table.Td
				fz={fzSmall}
				w={isMobile ? CW.mobile.child.fulfilled : CW.desktop.child.fulfilled}
				ta="center"
				c={color}
				style={{ whiteSpace: "nowrap" }}
			>
				<Group justify="center">
					{toLocal(parent.dateFulfilled, isMobile)}
					{parent.submissionStatus === "Uploaded" && (
						<>
							<Tooltip label="Watch final video">
								<Button
									onClick={() => setVideoModalOpen(true)}
									size={isMobile ? "compact-xs" : "compact-sm"}
									variant="default"
								>
									<IconPlayerPlay size={isMobile ? 14 : 16} />
								</Button>
							</Tooltip>
							<VideoModal
								videoPath={`${appSettings.S3BucketBaseUrl}SubmissionVideos/${parent.id}.mp4`}
								isOpen={videoModalOpened}
								setShowModal={setVideoModalOpen}
							/>
						</>
					)}
				</Group>
			</Table.Td>
		</Table.Tr>
	);
};

/* --------- NEW: Feedback row — shows who is assigned to give feedback --------- */

const FeedbackRow: React.FC<{
	isMobile: boolean;
	fzSmall: number;
	parent: ProjectSubmission;
}> = ({ isMobile, fzSmall, parent }) => {
	const [videoModalOpened, setVideoModalOpen] = useState<boolean>(false);
	const theme = useMantineTheme();

	// Use instrumentalist due date as the “due” reference for color, or adjust if you track a specific feedback due date
	const color = statusColor(theme, parent.feedbackDateFulfilled, parent.instrumentalistDueDate);

	const feedbackUser = parent.feedbackProvider ?? null;
	const submittedText = toLocal(parent.feedbackDateFulfilled, isMobile);
	const canWatch = parent.feedbackStatus === "Uploaded";

	return (
		<Table.Tr>
			{/* Indent */}
			<Table.Td w={isMobile ? CW.mobile.child.indent : CW.desktop.child.indent} />

			{/* Role label */}
			<Table.Td
				fz={fzSmall}
				w={isMobile ? CW.mobile.child.instrumentName : CW.desktop.child.instrumentName}
				ta="center"
			>
				<Text fz={Math.max(10, fzSmall * 0.95)} c={theme.colors.blue[6]} fw={500}>
					Feedback
				</Text>
			</Table.Td>

			{/* Assigned feedback provider (no offer list per your requirement) */}
			<Table.Td fz={fzSmall} w={isMobile ? CW.mobile.child.assignee : CW.desktop.child.assignee}>
				{feedbackUser ? (
					<Text fz={fzSmall}>
						{Utils.formatUser(feedbackUser)}
						{feedbackUser.email ? ` (${feedbackUser.email})` : ""}
					</Text>
				) : (
					<Text fz={fzSmall} c="dimmed">
						Unassigned
					</Text>
				)}
			</Table.Td>

			{/* Submitted + watch feedback video if uploaded */}
			<Table.Td
				fz={fzSmall}
				w={isMobile ? CW.mobile.child.fulfilled : CW.desktop.child.fulfilled}
				ta="center"
				c={color}
				style={{ whiteSpace: "nowrap" }}
			>
				<Group justify="center">
					{submittedText}
					{canWatch && (
						<>
							<Tooltip label="Watch feedback video">
								<Button
									onClick={() => setVideoModalOpen(true)}
									size={isMobile ? "compact-xs" : "compact-sm"}
									variant="default"
								>
									<IconPlayerPlay size={isMobile ? 14 : 16} />
								</Button>
							</Tooltip>
							<VideoModal
								videoPath={`${appSettings.S3BucketBaseUrl}SubmissionFeedbackVideos/${parent.id}.mp4`}
								isOpen={videoModalOpened}
								setShowModal={setVideoModalOpen}
							/>
						</>
					)}
				</Group>
			</Table.Td>
		</Table.Tr>
	);
};

/* --------------------------------- main comp -------------------------------- */

const ApprovedSubmissions: React.FC = () => {
	const [submissions, setSubmissions] = useState<ProjectSubmission[] | undefined>(undefined);
	const [page, setPage] = useState<number>(1);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(true);

	const theme = useMantineTheme();
	const isMobile = !!useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const fzSmall = isMobile ? 10 : 14;
	const fzMed = isMobile ? 14 : 18;

	const session = useContext(SessionContext);
	if (!session) return null;

	const FILTERS = {
		approvedOnly: true,
		unapprovedOnly: false,
		fulfilledOnly: false,
		associatedUserId: undefined as string | undefined,
		fulfilledTasksOnly: false,
		unfulfilledTasksOnly: false,
		classroomId: undefined as string | undefined,
	};

	// skeleton rows
	const parentColCount = isMobile ? 3 : 4;
	const skeletonRows = useMemo(() => {
		const cell = <Skeleton height={70} ta="center" />;
		const row = (
			<Table.Tr bg={theme.colors.blue[1]}>
				<Table.Td fz={fzSmall} ta="center">
					{cell}
				</Table.Td>
				<Table.Td fz={fzSmall} ta="center">
					{cell}
				</Table.Td>
				{isMobile ? null : <Table.Td fz={fzSmall} ta="center">{cell}</Table.Td>}
				<Table.Td fz={fzSmall} ta="center">
					{cell}
				</Table.Td>
			</Table.Tr>
		);
		return Array.from({ length: 4 }, (_, i) => React.cloneElement(row, { key: `s${i}` }));
	}, [isMobile, theme.colors.blue, fzSmall]);

	const fetchPage = useCallback(
		async (p: number) => {
			if (!session.user || !session.token) return;
			try {
				setLoading(true);
				setSubmissions(undefined);

				const res = await session.getProjectSubmissionsPage(
					p,
					FILTERS.approvedOnly,
					FILTERS.unapprovedOnly,
					FILTERS.fulfilledOnly,
					FILTERS.associatedUserId,
					FILTERS.fulfilledTasksOnly,
					FILTERS.unfulfilledTasksOnly,
					FILTERS.classroomId,
					true // loadOffers
				);

				setSubmissions(res.items);
				setTotalPages(res.totalPages);
			} catch (err) {
				console.error("Error fetching project submissions:", err);
				Utils.displayNotification(
					"Project Submissions Load Failure",
					"Something went wrong when loading project submissions. Please try again later.",
					"red",
					<IconAlertCircle size={16} />,
					true,
					false
				);
				setSubmissions([]);
				setTotalPages(1);
			} finally {
				setLoading(false);
			}
		},
		[session]
	);

	useEffect(() => {
		void fetchPage(page);
	}, [page, fetchPage]);

	const handlePageChange = useCallback((newPage: number) => {
		setPage(newPage);
	}, []);

	const handleRefresh = useCallback(async () => {
		setSubmissions(undefined);
		session.clearProjectSubmissionsCache();
		if (page === 1) {
			fetchPage(1);
		} else {
			setPage(1);
		}
	}, [session, page, fetchPage]);

	// header counts (future-due offers only)
	let openInstrumentalistOffers = 0;
	let openEditorOffers = 0;
	(submissions ?? []).forEach((s) => {
		if (!s.videoEditor) openEditorOffers++;
		(s.instrumentalistTasks ?? []).forEach((t) => {
			if (!t.instrumentalist) openInstrumentalistOffers++;
		});
	});

	const rows = useMemo(() => {
		if (!submissions) return null;

		return submissions.map((row) => (
			<React.Fragment key={row.id}>
				{/* Parent row (always visible, no expand/collapse) */}
				<Table.Tr bg={theme.colors.blue[1]}>
					{/* Editor due date */}
					<Table.Td
						fz={fzSmall}
						w={isMobile ? CW.mobile.parent.dueDate : CW.desktop.parent.dueDate}
						ta="center"
					>
						<Stack align="center" gap={2}>
							<Text fz={fzSmall} fw={HEADER_FW}>
								Editor Due
							</Text>
							<Text fz={fzSmall}>{toLocal(row.editorDueDate, isMobile)}</Text>
						</Stack>
					</Table.Td>

					{/* Instrumentalist due date */}
					<Table.Td
						fz={fzSmall}
						w={isMobile ? CW.mobile.parent.dueDate : CW.desktop.parent.dueDate}
						ta="center"
					>
						<Stack align="center" gap={2}>
							<Text fz={fzSmall} fw={HEADER_FW}>
								Player Due
							</Text>
							<Text fz={fzSmall}>{toLocal(row.instrumentalistDueDate, isMobile)}</Text>
						</Stack>
					</Table.Td>

					{/* Names */}
					{isMobile ? (
						<Table.Td w={CW.mobile.parent.names} ta="center">
							<Stack gap={2} align="center">
								<Text fz={fzSmall} fw={HEADER_FW}>
									Info
								</Text>
								<Text fz={fzSmall}>
									{`${truncate(row.submissionProjectName || "N/A Project", 22)} by ${truncate(
										row.projectOwner?.username ?? "N/A",
										18
									)}`}
								</Text>
							</Stack>
						</Table.Td>
					) : (
						<>
							<Table.Td w={CW.desktop.parent.studentName} ta="center">
								<Stack gap={2} align="center">
									<Text fz={fzSmall} fw={HEADER_FW}>
										User Name
									</Text>
									<Text fz={fzSmall}>{row.projectOwner?.username || "N/A User"}</Text>
								</Stack>
							</Table.Td>
							<Table.Td w={CW.desktop.parent.projectName} ta="center">
								<Stack gap={2} align="center">
									<Text fz={fzSmall} fw={HEADER_FW}>
										Project Name
									</Text>
									<Text fz={fzSmall}>{row.submissionProjectName || "N/A Project"}</Text>
								</Stack>
							</Table.Td>
						</>
					)}

					{/* Spacer */}
					<Table.Td w={isMobile ? CW.mobile.parent.spacer : CW.desktop.parent.spacer} />
				</Table.Tr>

				{/* Children table (instrumentalist tasks + feedback row + editor row) */}
				<Table.Tr>
					<Table.Td colSpan={isMobile ? 3 : 4} p={0}>
						<Box style={{ overflowX: "auto", width: "100%" }}>
							<Table w="100%">
								<Table.Thead>
									<Table.Tr>
										<Table.Th w={isMobile ? CW.mobile.child.indent : CW.desktop.child.indent} />
										<Table.Th
											w={isMobile ? CW.mobile.child.instrumentName : CW.desktop.child.instrumentName}
											ta="center"
										>
											<Text fz={fzSmall} fw={HEADER_FW}>
												Role
											</Text>
										</Table.Th>
										<Table.Th
											w={isMobile ? CW.mobile.child.assignee : CW.desktop.child.assignee}
											ta="center"
										>
											<Text fz={fzSmall} fw={HEADER_FW}>
												{isMobile ? "Assignee / Offered" : "Assignee / Offered Users"}
											</Text>
										</Table.Th>
										<Table.Th
											w={isMobile ? CW.mobile.child.fulfilled : CW.desktop.child.fulfilled}
											ta="center"
										>
											<Group justify="center">
												<Text fz={fzSmall} fw={HEADER_FW}>
													{isMobile ? "Submitted" : "Clip Submission Date"}
												</Text>
											</Group>
										</Table.Th>
									</Table.Tr>
								</Table.Thead>

								<Table.Tbody>
									{(row.instrumentalistTasks ?? []).map((t) => (
										<TaskRow key={t.id} isMobile={isMobile} fzSmall={fzSmall} parent={row} task={t} />
									))}

									{/* NEW: Feedback row */}
									<FeedbackRow isMobile={isMobile} fzSmall={fzSmall} parent={row} />

									{/* Editor row (uses same in-cell offered UX) */}
									<EditorRow isMobile={isMobile} fzSmall={fzSmall} parent={row} />
								</Table.Tbody>
							</Table>
						</Box>
					</Table.Td>
				</Table.Tr>
			</React.Fragment>
		));
	}, [submissions, theme.colors.blue, fzSmall, isMobile]);

	return (
		<Box
			w="100%"
			h="100%"
			style={{
				display: "grid",
				gridTemplateRows: "auto 1fr auto",
				overflow: "hidden",
				minHeight: 0,
			}}
		>
			{/* Header with small counts */}
			<Group w="100%" justify="space-between" align="center" p="xs">
				<Stack gap={2}>
					<Text c={theme.colors.blue[0]}>Check on the progress of approved project submissions</Text>
					<Group gap="md">
						<Group gap={6}>
							<IconUsers size={fzSmall + 2} color={theme.colors.blue[0]} />
							<Text fz={fzSmall} c={theme.colors.blue[0]}>
								<strong>{openInstrumentalistOffers}</strong> instrumentalist offer
								{openInstrumentalistOffers === 1 ? "" : "s"} open
							</Text>
						</Group>
						<Group gap={6}>
							<IconUsers size={fzSmall + 2} color={theme.colors.blue[0]} />
							<Text fz={fzSmall} c={theme.colors.blue[0]}>
								<strong>{openEditorOffers}</strong> editor offer
								{openEditorOffers === 1 ? "" : "s"} open
							</Text>
						</Group>
					</Group>
				</Stack>

				<Button leftSection={<IconRefresh size={fzMed} />} onClick={handleRefresh} variant="default" loading={loading}>
					Refresh
				</Button>
			</Group>

			{/* Scroll window */}
			<ScrollArea
				h="100%"
				w="100%"
				bg={theme.colors.blue[0]}
				type="auto"
				viewportProps={{ style: { height: "100%" } }}
				styles={{ viewport: { overflowX: "hidden" } }}
			>
				<Table
					horizontalSpacing="md"
					verticalSpacing="xs"
					miw="auto"
					layout="fixed"
					style={{ tableLayout: "fixed" }}
					stickyHeader
					striped
					highlightOnHover
				>
					<Table.Tbody>
						{loading ? (
							skeletonRows
						) : rows && rows.length > 0 ? (
							rows
						) : (
							<Table.Tr>
								<Table.Td colSpan={parentColCount}>
									<Text fw={500} ta="center">
										No Project Submissions
									</Text>
								</Table.Td>
							</Table.Tr>
						)}
					</Table.Tbody>
				</Table>
			</ScrollArea>

			{/* Pagination */}
			{totalPages > 1 ? (
				<Group w="100%" justify="flex-end" align="center" p="xs">
					<Pagination total={totalPages} value={page} onChange={handlePageChange} size="sm" withEdges disabled={loading} />
				</Group>
			) : (
				<Box style={{ height: 0 }} />
			)}
		</Box>
	);
};

export default ApprovedSubmissions;
